import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ROTULO_MODALIDADE_NO_FORMULARIO } from '@arena/shared/schemas';
import { siteApi } from '../api/site.js';
import { telefoneFormatado } from '../agenda/formatos.js';
import { Icone } from '../ui/Icone.js';
import { Card, Feed, ItemDeFeed, Vazio } from '../ui/primitivos.jsx';
import { horarioPedido } from './formatos.js';
import { ModalDoPedido } from './ModalDoPedido.jsx';

/**
 * O card "Pedidos do site" de `#v-agenda` — os pendentes, com o botão de
 * responder ali mesmo, sem sair da grade.
 *
 * Consulta a cada 15 segundos, como a própria grade: um pedido do
 * formulário chega a qualquer hora, e "resposta em até 2 horas" começa a
 * contar quando ele chega, não quando alguém abre a tela do site.
 *
 * Quem decide se o card aparece é a página (a verificação é cosmética); o
 * servidor responde 403 a quem não pode ver a fila.
 */
const INTERVALO_MS = 15_000;
const QUANTOS = 5;

export function CardDePedidosDoSite() {
  const [aberto, setAberto] = useState<string | null>(null);
  const pendentes = useQuery({
    queryKey: ['site', 'pedidos', 'Pendente', QUANTOS],
    queryFn: () => siteApi.pedidos({ situacao: 'Pendente', limite: QUANTOS }),
    refetchInterval: INTERVALO_MS,
  });

  const total = pendentes.data?.total ?? 0;
  const agora = new Date();

  return (
    <Card
      titulo="Pedidos do site"
      dica={pendentes.data ? `${total} ${total === 1 ? 'pendente' : 'pendentes'}` : undefined}
    >
      {!pendentes.data || pendentes.data.itens.length === 0 ? (
        <Vazio>
          {pendentes.isPending ? 'Carregando os pedidos…' : 'Nenhum pedido aguardando resposta.'}
        </Vazio>
      ) : (
        <Feed>
          {pendentes.data.itens.map((pedido) => (
            <ItemDeFeed
              key={pedido.id}
              marca={
                <span className="sq" style={{ background: 'var(--serie-3)' }}>
                  <Icone nome="globe" />
                </span>
              }
              titulo={pedido.nome}
              subtitulo={[
                ROTULO_MODALIDADE_NO_FORMULARIO[pedido.modalidade],
                horarioPedido(pedido.inicio, agora, pendentes.data.fuso).toLowerCase(),
                pedido.telefone ? telefoneFormatado(pedido.telefone) : null,
              ]
                .filter(Boolean)
                .join(' · ')}
              direita={<b style={{ color: 'var(--ac-ink)' }}>Responder</b>}
              onClick={() => setAberto(pedido.id)}
            />
          ))}
        </Feed>
      )}
      {aberto && <ModalDoPedido id={aberto} onFechar={() => setAberto(null)} />}
    </Card>
  );
}
